/**
 * Push Notification Compose Tab
 *
 * Recipient is chosen by searching a UserManagement user, selecting which of their apps to notify
 * (one or many — a device belongs to exactly one app), then picking "All devices" or a single one,
 * instead of pasting a raw token. The picker resolves to UserID / DeviceID / DeviceToken / AppID;
 * an "Enter token manually" switch keeps the raw-token path for power users / testing.
 *
 * The appId matters: it is what selects the APNs topic (bundle id) for the send. A push with no
 * appId falls back to the default bundle id and iOS rejects it with DeviceTokenNotForTopic.
 */

import { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';
import { IconSend, IconClock, IconInfoCircle, IconSearch } from '@tabler/icons-react';
import { Stack, Group, Button, Alert, Select, MultiSelect, TextInput, Switch, Textarea, Loader, Text } from '../../../../../components/ui';
import { useHttpClient, UserService, UserPushDeviceService, useApplicationCatalogueService } from '@actschurch/ccms-sdk';
import type { AppManifest } from '@actschurch/ccms-sdk';
import type { PushTestTabProps, PushTestData, PushRecipient } from '../types';
import { decodePushBody, parsePushSpec } from '../types';
import { PRIORITIES } from '../../types';
import { PushImageField, PushSoundField } from '../../_shared/PushMediaFields';

type Opt = { value: string; label: string };

/** Sentinel for devices registered without an appId (legacy rows). */
const NO_APP = '(none)';

export function PushTestTab({ templates, onSend }: PushTestTabProps) {
  const httpClient = useHttpClient();
  const catalogueService = useApplicationCatalogueService();
  const [sending, setSending] = useState(false);

  // Apps come from the Application Catalogue — it is the registry of the constellation, so the picker
  // never drifts from what actually ships.
  const [catalogue, setCatalogue] = useState<AppManifest[]>([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // /catalogue/all is admin-only; fall back to the entitlement-filtered catalogue if refused.
        let apps: AppManifest[] = [];
        try {
          apps = await catalogueService.getAllApps();
        } catch {
          apps = await catalogueService.getCatalogue();
        }
        if (active) setCatalogue(apps || []);
      } catch {
        // Non-fatal: the picker falls back to the raw appId recorded on each device.
        if (active) setCatalogue([]);
      }
    })();
    return () => { active = false; };
  }, [catalogueService]);

  /** Display name for an appId, from the catalogue; unknown ids fall back to the raw id. */
  const appLabel = (appId: string) => {
    if (appId === NO_APP) return 'Unknown app (no appId)';
    const app = catalogue.find((a) => a.appId?.toLowerCase() === appId.toLowerCase());
    return app?.displayName || app?.shortName || appId;
  };

  // Recipient picker state
  const [manualMode, setManualMode] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState<Opt[]>([]);
  const [userSearching, setUserSearching] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserLabel, setSelectedUserLabel] = useState('');
  const [devices, setDevices] = useState<any[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [selectedApps, setSelectedApps] = useState<string[]>([]); // empty = every app the user has
  const [manualAppId, setManualAppId] = useState<string | null>(null);
  const [targetDevice, setTargetDevice] = useState<string>('all'); // 'all' | deviceId

  const form = useForm<PushTestData>({
    initialValues: { useTemplate: false, templateId: undefined, userId: '', deviceToken: '', deviceID: '', title: '', body: '', imageUrl: '', sound: 'default', badge: '', data: {}, priority: 5, scheduledDateTime: undefined, addToQueue: true },
    validate: {
      title: (value) => (!value || value.trim() === '' ? 'Title is required' : null),
      body: (value) => (!value || value.trim() === '' ? 'Message is required' : null),
    },
  });

  useEffect(() => {
    if (form.values.useTemplate && form.values.templateId) {
      const template = templates.find((t) => t.templateID === form.values.templateId);
      if (template) {
        // The template is a notification spec — pull the title/body OUT of it. Dropping the raw JSON
        // into the message box is how a push ended up being sent as literal JSON.
        const decoded = decodePushBody(template.body);
        const spec = parsePushSpec(decoded);

        form.setFieldValue('title', spec?.title || template.templateName || '');
        form.setFieldValue('body', spec?.body ?? decoded);

        // A template that names its mobile app pre-selects it — that app is what picks the APNs topic,
        // so the template already knows the answer and the operator shouldn't have to repeat it.
        if (template.mobileAppId) {
          setSelectedApps([template.mobileAppId]);
          setManualAppId(template.mobileAppId);
          setTargetDevice('all');
        }
      }
    }
  }, [form.values.useTemplate, form.values.templateId, templates]);

  // Debounced user search (UserManagement).
  useEffect(() => {
    const q = userSearch.trim();
    if (q.length < 2) { setUserResults([]); return; }
    let active = true;
    setUserSearching(true);
    const t = setTimeout(async () => {
      try {
        const users = await new UserService(httpClient).search({ searchTerm: q, page: 1, pageSize: 10 });
        if (!active) return;
        setUserResults((users || []).map((u: any) => ({
          value: u.id,
          label: [u.fullName || u.displayName || [u.firstName, u.surname].filter(Boolean).join(' ') || u.userName || u.id, u.email]
            .filter(Boolean).join(' · '),
        })));
      } catch {
        if (active) setUserResults([]);
      } finally {
        if (active) setUserSearching(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [userSearch, httpClient]);

  // Load the selected user's registered devices.
  useEffect(() => {
    if (!selectedUserId) { setDevices([]); setTargetDevice('all'); setSelectedApps([]); return; }
    let active = true;
    setDevicesLoading(true);
    (async () => {
      try {
        const list = await new UserPushDeviceService(httpClient).getByUserId(selectedUserId);
        if (active) { setDevices(list || []); setTargetDevice('all'); setSelectedApps([]); }
      } catch {
        if (active) setDevices([]);
      } finally {
        if (active) setDevicesLoading(false);
      }
    })();
    return () => { active = false; };
  }, [selectedUserId, httpClient]);

  const allActiveDevices = devices.filter((d) => d.isActive && d.pushToken);

  // Apps come from the CATALOGUE, not from the user's devices — otherwise a device registered without
  // an appId (a legacy row) leaves you with nothing to pick, which is exactly how a push ends up with
  // appId=(none) and gets sent to the default apns-topic.
  const catalogueApps = catalogue.filter((a) => a.enabled !== false);

  const deviceCountFor = (appId: string) =>
    allActiveDevices.filter((d) => (d.appId || '').toLowerCase() === appId.toLowerCase()).length;

  // The suffix is how many of THIS user's devices are registered against that app. A zero is not a
  // dead end — selecting the app stamps it onto a device that never recorded one — so say nothing
  // rather than showing a bare "(0)", which reads like the app is unavailable.
  const appOptions: Opt[] = catalogueApps.map((a) => {
    const name = a.displayName || a.shortName || a.appId;
    const count = deviceCountFor(a.appId);
    return {
      value: a.appId,
      label: count > 0 ? `${name} · ${count} device${count > 1 ? 's' : ''}` : name,
    };
  });

  // A device that never recorded its app can still be addressed — but only once you say which app it
  // is, which is what selecting a single app does. That app is stamped onto the outgoing push. Only unambiguous when exactly one app is chosen.
  const stampAppId = selectedApps.length === 1 ? selectedApps[0] : undefined;

  const activeDevices = selectedApps.length === 0
    ? allActiveDevices // nothing chosen = every device, as before
    : allActiveDevices.filter((d) =>
        d.appId
          ? selectedApps.some((a) => a.toLowerCase() === String(d.appId).toLowerCase())
          // An unassigned device is only targetable when a single app is selected to stamp onto it.
          : !!stampAppId
      );

  // "All" targets unique tokens — a user can have duplicate rows (re-registrations) sharing a token.
  const uniqueByToken = Array.from(new Map(activeDevices.map((d) => [d.pushToken, d])).values());

  // Devices that will still go out with no appId — i.e. unassigned, with no single app to stamp.
  const unroutableCount = activeDevices.filter((d) => !d.appId && !stampAppId).length;

  // Unassigned devices that WILL be rescued by the chosen app.
  const stampedCount = stampAppId ? activeDevices.filter((d) => !d.appId).length : 0;

  // Option values MUST be unique — key by the device PK (deviceId can repeat across rows).
  const seenDeviceVals = new Set<string>();
  const deviceOptions: Opt[] = [{ value: 'all', label: `All devices (${uniqueByToken.length})` }];
  for (const d of activeDevices) {
    const val = String(d.id ?? d.deviceId);
    if (seenDeviceVals.has(val)) continue;
    seenDeviceVals.add(val);
    const shownApp = d.appId || stampAppId || NO_APP;
    deviceOptions.push({
      value: val,
      label: `${appLabel(shownApp)}${!d.appId && stampAppId ? ' (assigned)' : ''} · ${d.platform || 'device'} · ${d.deviceName || d.deviceId} · …${String(d.pushToken).slice(-8)}`,
    });
  }

  // Keep the selected user visible after the result set changes, and dedupe by value (Mantine
  // rejects duplicate option values).
  const userData: Opt[] = (() => {
    const base = selectedUserId && !userResults.some((o) => o.value === selectedUserId)
      ? [...userResults, { value: selectedUserId, label: selectedUserLabel || selectedUserId }]
      : userResults;
    const seen = new Set<string>();
    return base.filter((o) => (o.value && !seen.has(o.value) ? (seen.add(o.value), true) : false));
  })();

  const resolveRecipients = (values: PushTestData): { recipients: PushRecipient[]; userId?: string } => {
    if (manualMode) {
      const tok = (values.deviceToken || '').trim();
      // Carry the app through: without an appId the sender falls back to the DEFAULT apns-topic,
      // and iOS rejects the push with DeviceTokenNotForTopic for every app but the default one.
      return {
        recipients: tok ? [{ deviceToken: tok, deviceID: values.deviceID || '', appId: manualAppId || undefined }] : [],
        userId: values.userId,
      };
    }
    if (!selectedUserId) return { recipients: [] };
    const chosen = targetDevice === 'all'
      ? uniqueByToken
      : activeDevices.filter((d) => String(d.id ?? d.deviceId) === targetDevice);
    return {
      // Fall back to the single selected app for devices that never recorded one — without an appId
      // the sender uses the DEFAULT apns-topic and iOS rejects it with DeviceTokenNotForTopic.
      recipients: chosen.map((d) => ({
        deviceToken: d.pushToken,
        deviceID: d.deviceId,
        appId: d.appId || stampAppId,
        platform: d.platform,
      })),
      userId: selectedUserId,
    };
  };

  const recipientCount = manualMode
    ? ((form.values.deviceToken || '').trim() ? 1 : 0)
    : (selectedUserId ? (targetDevice === 'all' ? uniqueByToken.length : 1) : 0);

  const resetAll = () => {
    form.reset();
    setSelectedUserId(null); setSelectedUserLabel(''); setDevices([]);
    setUserResults([]); setUserSearch(''); setTargetDevice('all'); setSelectedApps([]); setManualAppId(null);
  };

  const handleSubmit = async (values: PushTestData, addToQueue: boolean) => {
    const { recipients, userId } = resolveRecipients(values);
    if (recipients.length === 0) return;
    setSending(true);
    try {
      await onSend({ ...values, recipients, userId, addToQueue });
      resetAll();
    } catch {
      // surfaced by the caller
    } finally {
      setSending(false);
    }
  };

  const templateOptions = templates.filter((t) => t.isActive).map((t) => ({ value: String(t.templateID), label: t.templateName || 'Unnamed' }));
  const priorityOptions = PRIORITIES.map((p) => ({ value: String(p.value), label: p.label }));
  const canSend = form.isValid() && recipientCount > 0;

  return (
    <form onSubmit={form.onSubmit((values) => handleSubmit(values, true))}>
      <Stack gap="md">
        <Alert color="blue" icon={<IconInfoCircle size={16} />} title="Compose push notification">
          Search for a user, choose which of their apps to notify (one, several, or all), then send to every device of those apps or a specific one. Switch to manual entry to address a raw token.
        </Alert>

        {/* Recipient */}
        <Stack gap="xs">
          <Group justify="space-between" align="center">
            <Text fw={600} size="sm">Recipient</Text>
            <Switch label="Enter token manually" checked={manualMode} onChange={(e) => setManualMode(e.currentTarget.checked)} />
          </Group>

          {manualMode ? (
            <>
              <Group grow>
                <TextInput label="User ID (optional)" placeholder="user-123" {...form.getInputProps('userId')} />
                <TextInput label="Device Token" placeholder="FCM / APNs token" {...form.getInputProps('deviceToken')} />
              </Group>
              <TextInput label="Device ID (optional)" placeholder="device-abc-123" {...form.getInputProps('deviceID')} />
              <Select
                label="App"
                description="Which app this token belongs to. Required for iOS: it selects the APNs topic (bundle id)."
                placeholder="Select the app…"
                data={catalogue
                  .filter((a) => a.enabled !== false)
                  .map((a) => ({ value: a.appId, label: `${a.displayName || a.shortName || a.appId} · ${a.appId}` }))}
                value={manualAppId}
                onChange={setManualAppId}
                searchable
                clearable
              />
              {!manualAppId && (
                <Alert color="yellow" icon={<IconInfoCircle size={16} />}>
                  Without an app, the send falls back to the default bundle id — iOS will reject it with
                  <b> DeviceTokenNotForTopic</b> unless this token really belongs to the default app.
                </Alert>
              )}
            </>
          ) : (
            <>
              <Select
                label="Search user"
                placeholder="Type a name or email…"
                leftSection={<IconSearch size={16} />}
                data={userData}
                searchable
                searchValue={userSearch}
                onSearchChange={setUserSearch}
                value={selectedUserId}
                onChange={(v) => {
                  setSelectedUserId(v);
                  const opt = userResults.find((o) => o.value === v);
                  if (opt) setSelectedUserLabel(opt.label);
                }}
                filter={({ options }: any) => options}
                nothingFoundMessage={userSearching ? 'Searching…' : (userSearch.trim().length < 2 ? 'Type at least 2 characters' : 'No users found')}
                rightSection={userSearching ? <Loader size="xs" /> : undefined}
                clearable
              />

              {selectedUserId && (
                devicesLoading ? (
                  <Group gap="xs"><Loader size="xs" /><Text size="sm" c="dimmed">Loading devices…</Text></Group>
                ) : allActiveDevices.length === 0 ? (
                  <Alert color="yellow">This user has no active registered devices.</Alert>
                ) : (
                  <>
                    <MultiSelect
                      label="Apps"
                      description="Which app(s) to notify, from the Application Catalogue. Any count shown is how many devices this user has registered for that app. Leave empty to send to every device."
                      placeholder={selectedApps.length === 0 ? 'All apps' : undefined}
                      data={appOptions}
                      value={selectedApps}
                      onChange={(v) => { setSelectedApps(v); setTargetDevice('all'); }}
                      clearable
                      searchable
                    />

                    <Select
                      label="Target device"
                      description="Send to every device of the chosen app(s), or pick one."
                      data={deviceOptions}
                      value={targetDevice}
                      onChange={(v) => setTargetDevice(v || 'all')}
                    />

                    {unroutableCount > 0 && (
                      <Alert color="yellow" icon={<IconInfoCircle size={16} />}>
                        {unroutableCount} device{unroutableCount > 1 ? 's are' : ' is'} registered without an app,
                        so the sender would fall back to the default bundle id — iOS rejects that with{' '}
                        <b>DeviceTokenNotForTopic</b>. <b>Select exactly one app above</b> to address{' '}
                        {unroutableCount > 1 ? 'them' : 'it'} correctly.
                      </Alert>
                    )}
                  </>
                )
              )}

              {recipientCount > 0 && (
                <Text size="xs" c="dimmed">
                  Will send to {recipientCount} device{recipientCount > 1 ? 's' : ''}
                  {selectedApps.length > 0 && ` across ${selectedApps.length} app${selectedApps.length > 1 ? 's' : ''}`}
                  {stampedCount > 0 && ` · ${stampedCount} device${stampedCount > 1 ? 's' : ''} will be sent as ${appLabel(stampAppId!)}`}.
                </Text>
              )}
            </>
          )}
        </Stack>

        <Switch label="Use Template" description="Load content from template" {...form.getInputProps('useTemplate', { type: 'checkbox' })} />
        {form.values.useTemplate && <Select label="Select Template" placeholder="Choose template" data={templateOptions} value={form.values.templateId ? String(form.values.templateId) : null} onChange={(value) => form.setFieldValue('templateId', value ? parseInt(value, 10) : undefined)} searchable clearable />}

        <TextInput label="Title" placeholder="Notification title" required {...form.getInputProps('title')} />
        <Textarea label="Message" placeholder="Notification message..." required rows={4} {...form.getInputProps('body')} />

        <Group grow>
          <PushImageField
            value={form.values.imageUrl || ''}
            onChange={(url) => form.setFieldValue('imageUrl', url)}
            recordId="push-compose"
          />
          <PushSoundField
            value={form.values.sound || 'default'}
            onChange={(sound) => form.setFieldValue('sound', sound)}
          />
        </Group>

        <TextInput label="Badge Count (Optional)" placeholder="1" {...form.getInputProps('badge')} />

        <Select label="Priority" description="Higher priority sent first" data={priorityOptions} value={String(form.values.priority)} onChange={(value) => form.setFieldValue('priority', parseInt(value || '5', 10))} />

        <TextInput label="Schedule Send (Optional)" placeholder="Leave empty for immediate" type="datetime-local" leftSection={<IconClock size={16} />} value={form.values.scheduledDateTime ? new Date(form.values.scheduledDateTime).toISOString().slice(0, 16) : ''} onChange={(e) => { const value = e.target.value; form.setFieldValue('scheduledDateTime', value ? new Date(value) : undefined); }} />

        <Group justify="flex-end" gap="md">
          <Button variant="subtle" onClick={resetAll} disabled={sending}>Clear</Button>
          <Button variant="outline" onClick={() => handleSubmit(form.values, true)} loading={sending} leftSection={<IconClock size={16} />} disabled={!canSend}>Add to Queue</Button>
          <Button type="submit" gradientVariant="info" loading={sending} leftSection={<IconSend size={16} />} disabled={!canSend}>Send Now</Button>
        </Group>
      </Stack>
    </form>
  );
}
